import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useAuth } from '../AuthContext';

// Rendered at "/" only while a guest is returning from Google sign-in.
// CRITICAL: we must stay on the "/?code=..." URL until Amplify has exchanged the
// authorization code for tokens. Navigating away early kills the exchange and the
// guest never gets signed in. So we wait for a confirmed session (Hub 'signedIn'
// event OR getCurrentUser succeeding) BEFORE sending them back to their order.
//
// We hand them back with a CLIENT-SIDE navigate (not a full reload): we only leave
// once refreshAuth() has populated `user` in context, so the order page mounts with
// the session already in place and resumes straight into the payment sheet — no
// black reload flash, no re-download of the bundle, no second loading screen.
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  useEffect(() => {
    const returnPath = localStorage.getItem('oauth_return_path') || '/';

    let done = false;
    const finish = async (failed) => {
      if (done) return;
      done = true;
      localStorage.removeItem('oauth_return_path');
      if (failed) {
        // Exchange failed — e.g. the guest returned in a different browser context
        // (in-app browser → Safari) so the PKCE verifier is gone. Flag it so the
        // order page re-prompts sign-in instead of stranding them on a loader.
        localStorage.setItem('oauthFailed', 'true');
      } else {
        // Make sure context.user is set before we leave, so the order page resumes
        // into payment immediately on mount (no propagation race).
        await refreshAuth();
      }
      navigate(returnPath, { replace: true });
    };

    // Primary signal: Amplify finished (or failed) the token exchange.
    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') finish(false);
      if (payload.event === 'signInWithRedirect_failure') finish(true);
    });

    // Backup signal: poll the session in case 'signedIn' fired before we mounted,
    // or the guest already had a live session.
    let attempts = 0;
    const check = async () => {
      if (done) return;
      attempts++;
      try {
        await getCurrentUser();
        finish(false); // tokens are stored — safe to leave
      } catch {
        if (attempts >= 40) finish(true); // ~12s — give up; order page re-prompts
        else setTimeout(check, 300);
      }
    };
    check();

    return () => { unsub(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={s.page}>
      <motion.div
        style={s.ring}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, ease: 'linear', duration: 0.9 }}
      />
      <h1 style={s.title}>Signing you in…</h1>
      <p style={s.sub}>Taking you back to your order</p>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif', padding: '24px' },
  ring: { width: '44px', height: '44px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.12)', borderTopColor: '#FFFFFF' },
  title: { fontSize: '20px', fontWeight: '700', color: '#fff', margin: 0 },
  sub: { fontSize: '14px', color: '#666', margin: 0 },
};
